import * as z from 'zod/mini'
import { ChangePasswordBody, keyedIssue, RequestEmailChangeBody, userFields } from '@seiri/shared/common'

const repeated = <T extends { password: string; password2: string }>() =>
  z.superRefine<T>((value, ctx) => {
    if (value.password2 !== value.password) {
      ctx.addIssue(keyedIssue('validation.passwordsMatch', ['password2']))
    }
  })

/**
 * Choosing a new password: the API's strength rule, typed twice. Only `password` is sent
 * (`setNewPasswordBody`); the repeat is checked here, reported on `password2`.
 */
export const newPasswordFormModel = z
  .object({ password: userFields.newPassword, password2: z.string() })
  .check(repeated<{ password: string; password2: string }>())
export type NewPasswordFormModel = z.infer<typeof newPasswordFormModel>

/**
 * Changing the password while signed in (Môj profil, docs/account.md): the current one, then a new one typed twice.
 */
export const changePasswordFormModel = z
  .object({
    currentPassword: z.string().check(z.minLength(1)),
    password: userFields.newPassword,
    password2: z.string(),
  })
  .check(repeated<{ currentPassword: string; password: string; password2: string }>())
export type ChangePasswordFormModel = z.infer<typeof changePasswordFormModel>

export const emptyChangePasswordForm = (): ChangePasswordFormModel => ({
  currentPassword: '',
  password: '',
  password2: '',
})

export const toChangePasswordBody = (model: ChangePasswordFormModel): ChangePasswordBody => ({
  currentPassword: model.currentPassword,
  newPassword: model.password,
})

/** Asking to sign in with another e-mail (Môj profil, docs/account.md): the new address and the current password. */
export const changeEmailFormModel = z.object({
  newEmail: userFields.email,
  currentPassword: z.string().check(z.minLength(1)),
})
export type ChangeEmailFormModel = z.infer<typeof changeEmailFormModel>

export const toRequestEmailChangeBody = (model: ChangeEmailFormModel): RequestEmailChangeBody => ({
  newEmail: model.newEmail,
  currentPassword: model.currentPassword,
})
